/*
 * DrillRow
 *
 * A single row in the drill-down list (used at the Vessels, Voyages and Routes
 * levels). Tapping the row body advances/drills (or, at the route level,
 * selects). An optional trailing chevron signals "drill deeper"; an optional
 * trailing action slot (e.g. a kebab) and badge are supported.
 */

import React from "react";
import { Box, ListItemButton, Typography } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

export const DRILL_ROW_SELECTED_BG = "rgba(0,79,125,0.07)";

export default function DrillRow({
  icon,
  primary,
  primaryExtra,
  secondary,
  meta,
  badge,
  muted = false,
  selected = false,
  disabled = false,
  showChevron = true,
  onClick,
  action,
  // embedded: the parent draws the selection frame (left bar + background).
  embedded = false,
}) {
  return (
    <ListItemButton
      onClick={onClick}
      selected={selected}
      disabled={disabled}
      sx={{
        px: 1,
        py: 0.5,
        minHeight: secondary ? 44 : 34,
        gap: 1,
        ...(embedded
          ? {
              borderRadius: 0,
              "&.Mui-selected": { backgroundColor: "transparent" },
              "&.Mui-selected:hover": { backgroundColor: "rgba(0,79,125,0.04)" },
              "&:hover": { backgroundColor: "rgba(0,79,125,0.06)" },
            }
          : {
              borderRadius: "10px",
              mb: 0.25,
              borderLeft: selected ? "4px solid #004f7d" : "4px solid transparent",
              "&.Mui-selected": { backgroundColor: DRILL_ROW_SELECTED_BG },
              "&.Mui-selected:hover": { backgroundColor: "rgba(0,79,125,0.10)" },
              "&:hover": { backgroundColor: "rgba(0,79,125,0.06)" },
            }),
      }}
    >
      {icon ? (
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            color: muted ? "#7b8a99" : "#004f7d",
          }}
        >
          {icon}
        </Box>
      ) : null}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: muted ? "#7b8a99" : "#004f7d",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {primary}
          </Typography>
          {primaryExtra ?? null}
        </Box>
        {secondary ? (
          <Typography
            sx={{
              fontSize: "11px",
              color: "#8a98a6",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {secondary}
          </Typography>
        ) : null}
      </Box>

      {badge ?? null}

      {meta ? (
        <Typography
          sx={{ fontSize: "11px", color: "#8a98a6", flexShrink: 0, whiteSpace: "nowrap" }}
        >
          {meta}
        </Typography>
      ) : null}

      {showChevron ? (
        <KeyboardArrowRightIcon sx={{ fontSize: 18, color: "#004f7d", flexShrink: 0 }} />
      ) : null}

      {action ? (
        <Box
          sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {action}
        </Box>
      ) : null}
    </ListItemButton>
  );
}
